import React, { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Reveal } from "./Reveal";
import { CONTACT, EVENT_TYPES } from "../data";
import { toast } from "sonner";
import { Loader2, Send, ChevronDown, Calendar as CalendarIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-b border-white/20 py-3 text-[#F2F2F2] placeholder-white/30 focus:border-[#003399] focus:outline-none transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    event_type: "",
    event_date: "",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickDate = (d) => {
    setDate(d);
    setField("event_date", d ? format(d, "PPP") : "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Inquiry sent — TAO LUMIN's team will be in touch soon.");
      setForm({ name: "", email: "", event_type: "", event_date: "", location: "", message: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 haze-radial opacity-60 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid md:grid-cols-12 gap-16">
          {/* Left */}
          <div className="md:col-span-5">
            <Reveal>
              <p className="text-[#003399] font-bold text-xs uppercase tracking-[0.3em] mb-6">
                Booking & Inquiries
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl md:text-6xl uppercase text-[#F2F2F2] leading-[1.05]">
                Let's make
                <br />
                <span className="text-outline">it move</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-[#F2F2F2]/65 font-light leading-relaxed max-w-sm">
                Clubs, private events, weddings and festivals. Tell us about your
                night and we'll bring the energy.
              </p>
            </Reveal>

            <div className="mt-12 space-y-6">
              <Reveal delay={0.25}>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${CONTACT.email}`}
                  className="block group"
                >
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#F2F2F2]/40">
                    Email
                  </span>
                  <span className="block font-display text-base md:text-lg text-[#F2F2F2] group-hover:text-[#003399] transition-colors">
                    {CONTACT.email}
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.3}>
                <a
                  data-testid="contact-phone-link"
                  href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                  className="block group"
                >
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#F2F2F2]/40">
                    Phone
                  </span>
                  <span className="block font-display text-base md:text-lg text-[#F2F2F2] group-hover:text-[#003399] transition-colors">
                    {CONTACT.phone}
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.35}>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#F2F2F2]/40">
                    Based in
                  </span>
                  <span className="block font-display text-base md:text-lg text-[#F2F2F2]">
                    {CONTACT.base}
                  </span>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Form */}
          <Reveal delay={0.15} className="md:col-span-7">
            <form onSubmit={submit} data-testid="booking-form" className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <input
                  data-testid="input-name"
                  className={FIELD}
                  placeholder="Your name *"
                  value={form.name}
                  onChange={set("name")}
                />
                <input
                  data-testid="input-email"
                  type="email"
                  className={FIELD}
                  placeholder="Email address *"
                  value={form.email}
                  onChange={set("email")}
                />
                <Select
                  value={form.event_type}
                  onValueChange={(v) => setField("event_type", v)}
                >
                  <SelectTrigger
                    data-testid="input-event-type"
                    className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-[#F2F2F2] focus:ring-0 focus:border-[#003399] data-[placeholder]:text-white/30 [&>svg]:hidden"
                  >
                    <SelectValue placeholder="Event type" />
                    <ChevronDown size={16} className="text-white/40 shrink-0" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="event-type-options"
                    className="bg-[#0A0A0A] border border-white/15 text-[#F2F2F2]"
                  >
                    {EVENT_TYPES.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`event-type-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                        className="focus:bg-[#003399] focus:text-[#F2F2F2] cursor-pointer"
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="input-event-date"
                      className={`${FIELD} flex items-center justify-between text-left ${
                        form.event_date ? "text-[#F2F2F2]" : "text-white/30"
                      }`}
                    >
                      {form.event_date || "Preferred date"}
                      <CalendarIcon size={16} className="text-white/40" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    data-testid="event-date-calendar"
                    className="w-auto p-0 bg-[#0A0A0A] border border-white/15 text-[#F2F2F2]"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={onPickDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <input
                data-testid="input-location"
                className={FIELD}
                placeholder="Location / venue"
                value={form.location}
                onChange={set("location")}
              />
              <textarea
                data-testid="input-message"
                rows={4}
                className={`${FIELD} resize-none`}
                placeholder="Tell us about your event *"
                value={form.message}
                onChange={set("message")}
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                data-testid="booking-submit-btn"
                className="inline-flex items-center gap-3 px-10 py-4 bg-[#003399] text-[#F2F2F2] text-sm uppercase tracking-[0.2em] hover:shadow-[0_0_32px_rgba(0,51,153,0.85)] transition-shadow duration-300 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry <Send size={16} />
                  </>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
